import React, {useState, useEffect, useRef} from "react";
import axios from "axios";
import {AutoComplete} from "primereact/autocomplete";
import {Calendar} from "primereact/calendar";
import {InputNumber} from "primereact/inputnumber";
import {Checkbox} from "primereact/checkbox";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {FileUpload} from "primereact/fileupload";
import {Toast} from "primereact/toast";
import {ProgressSpinner} from "primereact/progressspinner";

import {
	base,
	partners as partnersUrl,
	demand_letters,
	supply_contracts,
	additional_contracts,
	locations_url,
	projects_with_budget,
} from "../../../api";

export default function AdditionalContractDialog({setDialogVisibility, loadContent, toast}) {
	const [contractData, setContractData] = useState({
		deliveryTime: "",
		warrantyPeriod: "",
		paymentPeriod: "",
		startEndDate: {start: null, end: null},
		contractDate: null,
		prepaymentPercentage: null,
		file: null,
	});

	const [withoutVAT, setWithoutVAT] = useState(false);
	const [selectedProject, setSelectedProject] = useState(null);
	const [selectedContract, setSelectedContract] = useState(null);
	const [selectedPartner, setSelectedPartner] = useState(null);
	const [selectedLocation, setSelectedLocation] = useState(null);

	const [demandLetters, setDemandLetters] = useState([]);
	const [filteredDemandLettersForProject, setFilteredDemandLettersForProject] = useState([]);
	const [selectedDemandLetter, setSelectedDemandLetter] = useState(null);

	const [demandLetterProducts, setDemandLetterProducts] = useState([]);
	const [removedProducts, setRemovedProducts] = useState([]);

	const [partners, setPartners] = useState([]);
	const [filteredPartners, setFilteredPartners] = useState([]);
	const [projects, setProjects] = useState([]);
	const [filteredProjects, setFilteredProjects] = useState([]);
	const [contractOptions, setContractOptions] = useState([]);
	const [filteredContractOptions, setFilteredContractOptions] = useState([]);
	const [locations, setLocations] = useState([]);
	const [filteredLocations, setFilteredLocations] = useState([]);

	const [uploadedFile, setUploadedFile] = useState(null);

	const [loading, setLoading] = useState(false);

	const toastRef = useRef(null);

	useEffect(() => {
		axios
			.get(base + partnersUrl)
			.then(r => setPartners(r.data))
			.catch(() => {
				toast.current?.show({
					severity: "error",
					summary: "Error",
					detail: "Failed to load partners.",
					life: 3000,
				});
			});

		axios
			.get(base + projects_with_budget)
			.then(r => setProjects(r.data))
			.catch(() => {
				toast.current?.show({
					severity: "error",
					summary: "Error",
					detail: "Failed to load projects.",
					life: 3000,
				});
			});

		axios
			.get(base + demand_letters)
			.then(r => setDemandLetters(r.data))
			.catch(() => {
				toast.current?.show({
					severity: "error",
					summary: "Error",
					detail: "Failed to load demand letters.",
					life: 3000,
				});
			});
	}, []);

	useEffect(() => {
		axios
			.get(base + locations_url)
			.then(r => {
				setLocations(r.data);
				// Once locations are loaded, set the default location if selectedContract is available
				if (selectedContract) {
					const locationObj = r.data.find(l => l.id === selectedContract.location);
					setSelectedLocation(locationObj);
				}
			})
			.catch(() => {
				toast.current?.show({
					severity: "error",
					summary: "Error",
					detail: "Failed to load locations.",
					life: 3000,
				});
			});
	}, [selectedContract]); // Add selectedContract as a dependency

	useEffect(() => {
		if (selectedProject?.id) {
			axios
				.get(`${base + supply_contracts}?project_id=${selectedProject.id}/`)
				.then(r => setContractOptions(r.data))
				.catch(() => {
					setContractOptions([]);
					setSelectedContract(null);
					toast.current?.show({
						severity: "error",
						summary: "Error",
						detail: "Failed to load contracts for the selected project.",
						life: 3000,
					});
				});
		} else {
			setContractOptions([]);
			setSelectedContract(null);
		}
	}, [selectedProject]);

	useEffect(() => {
		if (selectedProject && selectedContract) {
			const filtered = demandLetters.filter(dl => dl.project === selectedProject.name);
			setFilteredDemandLettersForProject(filtered);
			setSelectedDemandLetter(null);
		} else {
			setFilteredDemandLettersForProject([]);
			setSelectedDemandLetter(null);
		}
	}, [selectedProject, selectedContract, demandLetters]);

	useEffect(() => {
		if (selectedDemandLetter) {
			const initialProducts =
				selectedDemandLetter.demand_letter_products?.map(p => ({
					...p,
					price: p.price || 0,
					count: p.count || 1,
				})) || [];
			setDemandLetterProducts(initialProducts);
			setRemovedProducts([]);
		} else {
			setDemandLetterProducts([]);
			setRemovedProducts([]);
		}
	}, [selectedDemandLetter]);

	const handleChange = (e, field) => {
		const value = e.value !== undefined ? e.value : e.target.value;
		setContractData(prev => ({...prev, [field]: value}));
	};

	const removeProduct = rowData => {
		setDemandLetterProducts(prev => prev.filter(p => p.id !== rowData.id));
		setRemovedProducts(prev => [...prev, rowData]);
	};

	const reAddProduct = rowData => {
		setRemovedProducts(prev => prev.filter(p => p.id !== rowData.id));
		setDemandLetterProducts(prev => [...prev, rowData]);
	};

	const handleCountChange = (productId, newCount) => {
		setDemandLetterProducts(prev =>
			prev.map(p => (p.id === productId ? {...p, count: newCount} : p)),
		);
		setRemovedProducts(prev => prev.map(p => (p.id === productId ? {...p, count: newCount} : p)));
	};

	const handlePriceChange = (productId, newPrice) => {
		setDemandLetterProducts(prev =>
			prev.map(p => (p.id === productId ? {...p, price: newPrice} : p)),
		);
		setRemovedProducts(prev => prev.map(p => (p.id === productId ? {...p, price: newPrice} : p)));
	};

	const handleFileUpload = e => {
		if (e.files && e.files.length > 0) {
			setUploadedFile(e.files[0]);
			toast.current?.show({
				severity: "info",
				summary: "File Selected",
				detail: e.files[0].name,
				life: 3000,
			});
		}
	};
	const onFileSelect = e => {
		setContractData(prev => ({...prev, file: e.files[0]}));
	};
	const saveData = async () => {
		if (!selectedProject || !selectedContract || !selectedPartner || !selectedLocation) {
			toast.current?.show({
				severity: "error",
				summary: "Error",
				detail: "Please select a project, contract, partner, and location.",
				life: 3000,
			});
			return;
		}

		setLoading(true);

		const requestData = {
			partner: selectedPartner?.id,
			contract: selectedContract?.id,
			location: selectedLocation?.id,
			advance_payment: contractData.prepaymentPercentage || 0,
			contract_date: contractData.contractDate
				? contractData.contractDate.toISOString().split("T")[0]
				: null,
			delivery_time: parseInt(contractData.deliveryTime, 10) || 0,
			start_date: contractData.startEndDate.start
				? contractData.startEndDate.start.toISOString().split("T")[0]
				: null,
			end_date: contractData.startEndDate.end
				? contractData.startEndDate.end.toISOString().split("T")[0]
				: null,
			warranty_period: parseInt(contractData.warrantyPeriod, 10) || 0,
			payment_period: parseInt(contractData.paymentPeriod, 10) || 0,
			without_vat: withoutVAT,
			purchase_price: 0,
			demand_letter_id: selectedDemandLetter?.id || null,
			contract_products: demandLetterProducts.map(prod => ({
				product: prod.product,
				count: prod.count,
				description: prod.description,
				unit: prod.unit,
				price: prod.price || 0,
			})),
		};

		try {
			const postResponse = await axios.post(base + additional_contracts, requestData, {
				headers: {"Content-Type": "application/json"},
			});

			const newContractID = postResponse.data.id;

			if (contractData.file && newContractID) {
				const formData = new FormData();
				formData.append("approved_document", contractData.file);

				await axios.patch(`${base + additional_contracts}/${newContractID}/`, formData, {
					headers: {"Content-Type": "multipart/form-data"},
				});

				toast.current?.show({
					severity: "success",
					summary: "Success",
					detail: "Contract saved and file uploaded successfully!",
					life: 3000,
				});
			} else {
				toast.current?.show({
					severity: "success",
					summary: "Success",
					detail: "Contract saved successfully!",
					life: 3000,
				});
			}

			loadContent();
			setDialogVisibility(false);
		} catch (error) {
			console.error("Error saving contract:", error);
			if (error.response && error.response.data) {
				toast.current?.show({
					severity: "error",
					summary: "Error",
					detail: error.response.data.message || "Failed to save contract.",
					life: 3000,
				});
			} else {
				toast.current?.show({
					severity: "error",
					summary: "Error",
					detail: "An unexpected error occurred.",
					life: 3000,
				});
			}
		} finally {
			setLoading(false);
		}
	};

	const searchProject = event => {
		const q = event.query.toLowerCase();
		setFilteredProjects(
			projects.filter(
				proj =>
					proj.full_name_am.toLowerCase().includes(q) ||
					proj.name.toLowerCase().includes(q) ||
					String(proj.id).toLowerCase().includes(q),
			),
		);
	};

	const searchPartner = event => {
		const q = event.query.toLowerCase();
		setFilteredPartners(
			partners.filter(
				p =>
					(p.full_name_am?.toLowerCase() || "").includes(q) ||
					(p.company_am?.toLowerCase() || "").includes(q) ||
					(p.passport?.toLowerCase() || "").includes(q) ||
					(p.phone?.toLowerCase() || "").includes(q) ||
					String(p.id).toLowerCase().includes(q),
			),
		);
	};

	const searchContract = event => {
		if (!event.query.trim().length) {
			setFilteredContractOptions([...contractOptions]);
		} else {
			const q = event.query.toLowerCase();
			const filtered = contractOptions.filter(
				c => c.formatted_contract_number?.toLowerCase().includes(q) || c.id.toString().includes(q),
			);
			setFilteredContractOptions(filtered);
		}
	};

	const searchLocation = event => {
		const q = event.query.toLowerCase();
		setFilteredLocations(locations.filter(loc => loc.title_am.toLowerCase().includes(q)));
	};

	const totalPriceBody = rowData => {
		return <div>{(rowData.count * rowData.price).toLocaleString()}</div>;
	};
	console.log(selectedContract);

	useEffect(() => {
		if (selectedContract && locations.length > 0) {
			const locationObj = locations.find(l => l.id === selectedContract.location);

			if (locationObj) {
				setSelectedLocation(locationObj);
			} else {
				console.error("Location not found in locations array:", selectedContract.location);
				toast.current?.show({
					severity: "warn",
					summary: "Warning",
					detail: "Location not found in the list.",
					life: 3000,
				});
			}

			setContractData(prev => ({
				...prev,
				deliveryTime: selectedContract.delivery_time || "",
				warrantyPeriod: selectedContract.warranty_period || "",
				paymentPeriod: selectedContract.payment_period || "",
				startEndDate: {
					start: selectedContract.start_date ? new Date(selectedContract.start_date) : null,
					end: selectedContract.end_date ? new Date(selectedContract.end_date) : null,
				},
				contractDate: selectedContract.contract_date
					? new Date(selectedContract.contract_date)
					: null,
				prepaymentPercentage: selectedContract.advance_payment || null,
			}));

			setWithoutVAT(selectedContract.without_vat || false);

			if (selectedContract.demand_letter?.demand_letter_products) {
				const initialProducts = selectedContract.demand_letter.demand_letter_products.map(p => ({
					...p,
					price: p.price || 0,
					count: p.count || 1,
				}));
				setDemandLetterProducts(initialProducts);
			}
		}
	}, [selectedContract, locations]);

	return (
		<>
			<div className="field col-12 mb-3">
				<label htmlFor="project">Select Project</label>
				<AutoComplete
					id="project"
					value={selectedProject}
					suggestions={filteredProjects}
					completeMethod={searchProject}
					field="full_name_am"
					onChange={e => {
						setSelectedProject(e.value);
						setSelectedContract(null);
						setSelectedDemandLetter(null);
					}}
					placeholder="Search Project by Name or ID"
					dropdown
					className="w-100"
					panelStyle={{width: "300px"}}
					itemTemplate={item => (
						<div>
							<div className="text-dark">{item.full_name_am}</div>
							<small className="text-primary">
								{item.name || "No code available"} (ID: {item.id})
							</small>
						</div>
					)}
				/>
			</div>

			{selectedProject && (
				<div className="mb-3">
					<AutoComplete
						value={selectedContract}
						suggestions={filteredContractOptions}
						completeMethod={searchContract}
						field="formatted_contract_number"
						onChange={e => setSelectedContract(e.value)}
						placeholder="Select Contract"
						className="w-100"
						dropdown
						itemTemplate={item => (
							<div>
								<div className="text-dark">{item.formatted_contract_number}</div>
								<small className="text-primary">
									Contract Number: {item.contract_number} (ID: {item.id})
								</small>
							</div>
						)}
					/>
				</div>
			)}

			{selectedProject && selectedContract && (
				<div className="field col-12 mb-3">
					<label htmlFor="demandLetter">Select Demand Letter</label>
					<Dropdown
						id="demandLetter"
						value={selectedDemandLetter}
						options={filteredDemandLettersForProject}
						optionLabel="project_full_name_am"
						onChange={e => setSelectedDemandLetter(e.value)}
						placeholder="Select Demand Letter"
						className="w-100"
						panelStyle={{width: "300px"}}
						itemTemplate={item => (
							<div>
								<div className="text-dark">
									Project: {item.project_full_name_am || item.project}
								</div>
								<small className="text-primary">Date: {item.date}</small>
							</div>
						)}
					/>
				</div>
			)}

			{selectedProject && selectedContract && selectedDemandLetter && (
				<>
					<div className="p-fluid row p-2">
						{/* <div className="field col-3 mb-3">
							<label htmlFor="partner">Select Partner</label>
							<AutoComplete
								id="partner"
								value={selectedPartner}
								suggestions={filteredPartners}
								completeMethod={searchPartner}
								field="full_name_am"
								onChange={e => setSelectedPartner(e.value)}
								placeholder="Search Partner by Name"
								dropdown
								className="w-100"
								panelStyle={{width: "200px"}}
								itemTemplate={item => (
									<div className="d-grid">
										<div className="text-dark">{item.full_name_am || item.company_am}</div>
										<small className="text-primary">Passport: {item.passport || "-"}</small>
										<small className="text-primary">Phone: {item.phone || "-"}</small>
									</div>
								)}
							/>
						</div> */}
						<div className="field col-3 mb-3">
							<label htmlFor="location">Select Location</label>
							<AutoComplete
								id="location"
								value={selectedLocation}
								suggestions={filteredLocations}
								completeMethod={searchLocation}
								field="title_am"
								onChange={e => setSelectedLocation(e.value)}
								placeholder="Search Location by Name or ID"
								dropdown
								className="w-100"
								panelStyle={{width: "200px"}}
								itemTemplate={item => (
									<div className="d-grid">
										<div className="text-dark">{item.title_am}</div>
									</div>
								)}
							/>
						</div>

						<div className="field col-3">
							<label htmlFor="contractDate">Contract Date</label>
							<Calendar
								id="contractDate"
								value={contractData.contractDate}
								onChange={e => handleChange(e, "contractDate")}
								dateFormat="dd.mm.yy"
								placeholder="DD.MM.YYYY"
							/>
						</div>

						<div className="field col-3">
							<label htmlFor="deliveryTime">Delivery Time (days)</label>
							<InputNumber
								id="deliveryTime"
								value={contractData.deliveryTime}
								onValueChange={e => handleChange(e, "deliveryTime")}
								mode="decimal"
								min={0}
								placeholder="Days"
							/>
						</div>

						<div className="field col-3">
							<label htmlFor="warrantyPeriod">Warranty Period</label>
							<InputNumber
								id="warrantyPeriod"
								value={contractData.warrantyPeriod}
								onValueChange={e => handleChange(e, "warrantyPeriod")}
								mode="decimal"
								min={0}
								placeholder="Days"
							/>
						</div>

						<div className="field col-3">
							<label htmlFor="paymentPeriod">Payment Period</label>
							<InputNumber
								id="paymentPeriod"
								value={contractData.paymentPeriod}
								onValueChange={e => handleChange(e, "paymentPeriod")}
								mode="decimal"
								min={0}
								placeholder="Days"
							/>
						</div>

						<div className="field col-3">
							<label htmlFor="startEndDateStart">Start Date</label>
							<Calendar
								id="startEndDateStart"
								value={contractData.startEndDate.start}
								onChange={e =>
									setContractData(prev => ({
										...prev,
										startEndDate: {
											...prev.startEndDate,
											start: e.value,
										},
									}))
								}
								dateFormat="dd.mm.yy"
								placeholder="DD.MM.YYYY"
							/>
						</div>

						<div className="field col-3">
							<label htmlFor="startEndDateEnd">End Date</label>
							<Calendar
								id="startEndDateEnd"
								value={contractData.startEndDate.end}
								onChange={e =>
									setContractData(prev => ({
										...prev,
										startEndDate: {
											...prev.startEndDate,
											end: e.value,
										},
									}))
								}
								dateFormat="dd.mm.yy"
								placeholder="DD.MM.YYYY"
							/>
						</div>

						<div className="field col-3">
							<label htmlFor="prepaymentPercentage">Prepayment (%)</label>
							<InputNumber
								id="prepaymentPercentage"
								value={contractData.prepaymentPercentage}
								onValueChange={e => handleChange(e, "prepaymentPercentage")}
								mode="decimal"
								min={0}
								max={100}
								suffix="%"
							/>
						</div>

						<div className="field col-3 d-flex align-items-center mt-4 pt-4">
							<Checkbox
								inputId="withoutVAT"
								checked={withoutVAT}
								onChange={e => setWithoutVAT(e.checked)}
							/>
							<label htmlFor="withoutVAT" className="ml-2">
								Առանց ԱԱՀ
							</label>
						</div>
					</div>

					<div className="col-12 mt-3 mb-5">
						<label htmlFor="fileUpload">Upload File</label>
						<FileUpload
							name="file"
							auto
							customUpload
							uploadHandler={onFileSelect}
							chooseLabel="Select File"
							className="w-100"
						/>
					</div>

					<h5>Selected Demand Letter Products</h5>
					<DataTable value={demandLetterProducts} responsiveLayout="scroll" className="mb-3">
						<Column field="description" header="Description" className="col-4" />
						<Column
							header="Count"
							className="col-2 text-center"
							body={rowData => (
								<InputNumber
									value={rowData.count}
									onValueChange={e => handleCountChange(rowData.id, e.value)}
									mode="decimal"
									min={0}
								/>
							)}
						/>
						<Column
							header="Price"
							className="col-3 text-center"
							body={rowData => (
								<InputNumber
									value={rowData.price}
									onValueChange={e => handlePriceChange(rowData.id, e.value)}
									mode="decimal"
									min={0}
								/>
							)}
						/>
						<Column header="Total" className="col-3 text-center" body={totalPriceBody} />

						<Column
							header="Remove"
							className="col-3 text-center"
							body={rowData => (
								<Button
									icon="pi pi-trash"
									className="p-button-rounded p-button-danger p-button-text"
									onClick={() => removeProduct(rowData)}
								/>
							)}
						/>
					</DataTable>

					{removedProducts.length > 0 && (
						<>
							<h5>Removed Products</h5>
							<DataTable value={removedProducts} responsiveLayout="scroll" className="mb-3">
								<Column field="description" header="Description" className="col-4" />
								<Column
									header="Count"
									className="col-2 text-center"
									body={rowData => (
										<InputNumber
											value={rowData.count}
											onValueChange={e => handleCountChange(rowData.id, e.value)}
											mode="decimal"
											min={0}
										/>
									)}
								/>
								<Column
									header="Price"
									className="col-3 text-center"
									body={rowData => (
										<InputNumber
											value={rowData.price}
											onValueChange={e => handlePriceChange(rowData.id, e.value)}
											mode="decimal"
											min={0}
										/>
									)}
								/>
								<Column header="Total" className="col-3 text-center" body={totalPriceBody} />

								<Column
									header="Re-Add"
									className="col-3 text-center"
									body={rowData => (
										<Button
											icon="pi pi-refresh"
											className="p-button-rounded p-button-success p-button-text"
											onClick={() => reAddProduct(rowData)}
										/>
									)}
								/>
							</DataTable>
						</>
					)}

					<div className="col-12 text-center my-4">
						<Button
							label="Cancel"
							icon="pi pi-times"
							rounded
							className="p-button-secondary mx-2"
							onClick={() => setDialogVisibility(false)}
							disabled={loading}
						/>
						<Button
							label={loading ? "Saving..." : "Save"}
							icon={loading ? "pi pi-spin pi-spinner" : "pi pi-check"}
							rounded
							className="mx-2"
							onClick={saveData}
							disabled={loading}
						/>
					</div>
				</>
			)}
		</>
	);
}
